import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Send, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [dates, setDates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reminderMessage, setReminderMessage] = useState('');
  const [newDate, setNewDate] = useState({
    course_id: '',
    date: '',
    available_slots: 12
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBookings();
    fetchDates();
    fetchCourses();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    }
  };

  const fetchDates = async () => {
    try {
      const response = await axios.get(`${API}/admin/dates`);
      setDates(response.data);
    } catch (error) {
      console.error('Failed to fetch dates:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const handleSendReminder = async () => {
    if (!reminderMessage) {
      toast.error('Please enter a reminder message');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/send-reminder`, {
        booking_id: selectedBooking.id,
        message: reminderMessage
      });
      toast.success('Reminder sent successfully');
      setShowReminderDialog(false);
      setReminderMessage('');
      setSelectedBooking(null);
    } catch (error) {
      console.error('Failed to send reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDate = async () => {
    if (!newDate.course_id || !newDate.date) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/dates`, newDate);
      toast.success('Date added successfully');
      setShowDateDialog(false);
      setNewDate({ course_id: '', date: '', available_slots: 12 });
      fetchDates();
    } catch (error) {
      console.error('Failed to add date:', error);
      toast.error('Failed to add date');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDate = async (dateId) => {
    if (!window.confirm('Are you sure you want to delete this date?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/dates/${dateId}`);
      toast.success('Date deleted successfully');
      fetchDates();
    } catch (error) {
      console.error('Failed to delete date:', error);
      toast.error('Failed to delete date');
    }
  };

  const openReminderDialog = (booking) => {
    setSelectedBooking(booking);
    setReminderMessage(`This is a reminder about your upcoming ${booking.course_name} course on ${booking.date}. Please ensure you arrive at 9:30 AM.`);
    setShowReminderDialog(true);
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : courseId;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold" data-testid="admin-dashboard-title">Admin Dashboard</h1>
          <p className="text-slate-300 mt-1">North London College</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="bookings" data-testid="bookings-tab">Bookings</TabsTrigger>
            <TabsTrigger value="dates" data-testid="dates-tab">Manage Dates</TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" data-testid="bookings-content">
            <Card>
              <CardHeader>
                <CardTitle>All Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Course</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Exam</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center text-slate-500 py-8">
                            No bookings yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        bookings.map((booking) => (
                          <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
                            <TableCell className="font-medium">{booking.student_name}</TableCell>
                            <TableCell>{booking.student_email}</TableCell>
                            <TableCell>{booking.student_phone}</TableCell>
                            <TableCell>{booking.course_name}</TableCell>
                            <TableCell>{booking.date}</TableCell>
                            <TableCell>{booking.exam_bundle ? 'Yes' : 'No'}</TableCell>
                            <TableCell>£{booking.full_price.toFixed(2)}</TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                booking.payment_status === 'deposit_paid' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {booking.payment_status === 'deposit_paid' ? 'Deposit Paid' : 'Pending'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Button
                                data-testid={`send-reminder-btn-${booking.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => openReminderDialog(booking)}
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Send Reminder
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dates Tab */}
          <TabsContent value="dates" data-testid="dates-content">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Course Dates</CardTitle>
                <Button
                  data-testid="add-date-btn"
                  onClick={() => setShowDateDialog(true)}
                  className="bg-sky-600 text-white hover:bg-sky-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Date
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Course</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Available Slots</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dates.map((date) => (
                        <TableRow key={date.id} data-testid={`date-row-${date.id}`}>
                          <TableCell>{getCourseName(date.course_id)}</TableCell>
                          <TableCell>
                            {new Date(date.date).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </TableCell>
                          <TableCell>{date.available_slots}</TableCell>
                          <TableCell>
                            <Button
                              data-testid={`delete-date-btn-${date.id}`}
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteDate(date.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle>Send Reminder</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Student:</strong> {selectedBooking.student_name}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Email:</strong> {selectedBooking.student_email}
                </p>
              </div>
              <div>
                <Label htmlFor="reminder-message">Reminder Message</Label>
                <Textarea
                  id="reminder-message"
                  data-testid="reminder-message-input"
                  value={reminderMessage}
                  onChange={(e) => setReminderMessage(e.target.value)}
                  rows={5}
                  className="mt-1"
                />
              </div>
              <Button
                data-testid="send-reminder-confirm-btn"
                onClick={handleSendReminder}
                className="w-full bg-sky-600 text-white hover:bg-sky-700"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reminder'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Date Dialog */}
      <Dialog open={showDateDialog} onOpenChange={setShowDateDialog}>
        <DialogContent data-testid="add-date-dialog">
          <DialogHeader>
            <DialogTitle>Add Course Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="course-select">Course</Label>
              <select
                id="course-select"
                data-testid="course-select"
                value={newDate.course_id}
                onChange={(e) => setNewDate({ ...newDate, course_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-600"
              >
                <option value="">Select a course</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="date-input">Date</Label>
              <Input
                id="date-input"
                data-testid="date-input"
                type="date"
                value={newDate.date}
                onChange={(e) => setNewDate({ ...newDate, date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="slots-input">Available Slots</Label>
              <Input
                id="slots-input"
                data-testid="slots-input"
                type="number"
                value={newDate.available_slots}
                onChange={(e) => setNewDate({ ...newDate, available_slots: parseInt(e.target.value) })}
                className="mt-1"
                min="1"
              />
            </div>
            <Button
              data-testid="add-date-confirm-btn"
              onClick={handleAddDate}
              className="w-full bg-sky-600 text-white hover:bg-sky-700"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Date'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
